import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Sparkles, 
  Plus, 
  Clock, 
  DollarSign, 
  Crown,
  CheckCircle,
  ShoppingCart,
  ArrowRight
} from "lucide-react";
import axios from "axios";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const UpsellModal = ({ 
  isOpen, 
  onClose, 
  mainService, 
  stylistId,
  onAddServices,
  selectedServices = []
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bundleTotal, setBundleTotal] = useState(null);

  useEffect(() => {
    if (isOpen && mainService?.service_id && stylistId) {
      fetchSuggestions();
    }
  }, [isOpen, mainService, stylistId]);

  useEffect(() => {
    if (selectedAddons.length > 0) {
      calculateBundle();
    } else {
      setBundleTotal(null);
    }
  }, [selectedAddons]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/upsell/suggestions/${mainService.service_id}?stylist_id=${stylistId}`
      );
      setSuggestions(response.data.suggestions || []);
      setPrompt(response.data.prompt || "Enhance your service with these popular add-ons!");
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateBundle = async () => {
    try {
      const serviceIds = [mainService.service_id, ...selectedAddons.map(a => a.service_id)];
      const response = await axios.post(`${API}/api/upsell/calculate-bundle`, {
        service_ids: serviceIds,
        stylist_id: stylistId
      });
      setBundleTotal(response.data);
    } catch (error) {
      console.error("Error calculating bundle:", error);
    }
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.service_id === addon.service_id);
      if (exists) {
        return prev.filter(a => a.service_id !== addon.service_id);
      }
      return [...prev, addon];
    });
  };

  const handleConfirm = () => {
    const allServices = [mainService, ...selectedAddons];
    onAddServices(allServices, bundleTotal);
    onClose();
  };

  const handleSkip = () => {
    onAddServices([mainService], null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-amber-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold">
              <Crown className="w-3 h-3 mr-1" />
              Pro Feature
            </Badge>
          </div>
          <DialogTitle className="text-xl font-bold text-white">
            Enhance Your Experience
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {prompt}
          </DialogDescription>
        </DialogHeader>

        {/* Main Service */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-amber-400" />
              <div>
                <h4 className="font-semibold text-white">{mainService?.name}</h4>
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {mainService?.duration_minutes} min
                  </span>
                </div>
              </div>
            </div>
            <div className="text-lg font-bold text-amber-400">
              ${mainService?.base_price?.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-400">Finding perfect add-ons for you...</p>
          </div>
        ) : suggestions.length > 0 ? (
          <>
            {/* Suggested Add-ons */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                Popular Add-ons
              </h4>
              {suggestions.map((addon) => {
                const isSelected = selectedAddons.find(a => a.service_id === addon.service_id);
                return (
                  <Card 
                    key={addon.service_id}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? "bg-amber-500/20 border-amber-500" 
                        : "bg-stone-800/50 border-stone-700 hover:border-amber-500/50"
                    }`}
                    onClick={() => toggleAddon(addon)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={!!isSelected}
                            className="border-amber-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black"
                          />
                          <div>
                            <h5 className="font-medium text-white">{addon.name}</h5>
                            {addon.description && (
                              <p className="text-sm text-gray-400 line-clamp-1">{addon.description}</p>
                            )}
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                +{addon.duration_minutes} min
                              </span>
                              {addon.is_addon && (
                                <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-400">
                                  Add-on Special
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {addon.addon_price && addon.addon_price < addon.base_price ? (
                            <div>
                              <span className="text-gray-500 line-through text-sm">
                                ${addon.base_price?.toFixed(2)}
                              </span>
                              <div className="text-lg font-bold text-emerald-400">
                                ${addon.addon_price?.toFixed(2)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-bold text-white">
                              ${(addon.addon_price || addon.base_price)?.toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Bundle Total */}
            {bundleTotal && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">
                      {bundleTotal.services?.length} services • {bundleTotal.total_duration} min
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-400">
                      ${bundleTotal.total_price?.toFixed(2)}
                    </div>
                    {bundleTotal.savings > 0 && (
                      <div className="text-xs text-emerald-400">
                        You save ${bundleTotal.savings?.toFixed(2)}!
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p>No add-on services available at this time.</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1 border-stone-600 text-gray-300 hover:bg-stone-800"
          >
            Skip Add-ons
          </Button>
          <Button
            onClick={handleConfirm}
            className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold"
          >
            {selectedAddons.length > 0 ? (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add {selectedAddons.length} Service{selectedAddons.length > 1 ? "s" : ""}
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpsellModal;
